import { Capacitor } from "@capacitor/core";
import {
  CustomerInfo,
  Purchases,
  PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { trackAnalyticsEvent } from "./analytics";
import { observerBackendOrigin } from "./runtimeConfig";

export type MembershipPlan = "plus" | "pro";

export interface NativeMembershipPrice {
  plan: MembershipPlan;
  price: string;
  productId: string;
}

const productIds: Record<MembershipPlan, string> = {
  plus: "observer_plus_monthly",
  pro: "observer_pro_monthly",
};

let configured = false;
let configuredUserId: string | null = null;

const platformKey = () => {
  if (Capacitor.getPlatform() === "ios")
    return process.env.REACT_APP_REVENUECAT_IOS_API_KEY || "";
  if (Capacitor.getPlatform() === "android")
    return process.env.REACT_APP_REVENUECAT_ANDROID_API_KEY || "";
  return "";
};

export const nativeBillingAvailable = () =>
  Capacitor.isNativePlatform() && Boolean(platformKey());

async function configure(userId?: string | null) {
  if (!Capacitor.isNativePlatform())
    throw new Error("Native billing is only available in the mobile app.");
  const apiKey = platformKey();
  if (!apiKey)
    throw new Error("Billing has not been provisioned for this app store.");

  if (!configured) {
    await Purchases.configure({
      apiKey,
      appUserID: userId || null,
      automaticDeviceIdentifierCollectionEnabled: false,
      diagnosticsEnabled: false,
    });
    configured = true;
    configuredUserId = userId || null;
  } else if (userId && configuredUserId !== userId) {
    await Purchases.logIn({ appUserID: userId });
    configuredUserId = userId;
  }
}

async function packageForPlan(
  plan: MembershipPlan,
  userId?: string | null,
): Promise<PurchasesPackage> {
  await configure(userId);
  const offerings = await Purchases.getOfferings();
  const current = offerings.current;
  if (!current)
    throw new Error("No Observer membership is available in this storefront.");
  const found = current.availablePackages.find(
    (item) =>
      item.identifier === `${plan}_monthly` ||
      item.product.identifier === productIds[plan],
  );
  if (!found)
    throw new Error("That membership is not available in this storefront.");
  return found;
}

const activeTier = (customerInfo: CustomerInfo): MembershipPlan | "free" => {
  if (customerInfo.entitlements.active.pro) return "pro";
  if (customerInfo.entitlements.active.plus) return "plus";
  return "free";
};

async function reconcile(idToken?: string) {
  if (!idToken) return;
  const response = await fetch(
    `${observerBackendOrigin}/api/observer/billing/reconcile`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
    },
  );
  if (!response.ok)
    throw new Error("Your purchase succeeded, but access is still syncing.");
}

export async function loadNativeMembershipPrices(
  userId?: string | null,
): Promise<Record<MembershipPlan, NativeMembershipPrice>> {
  const plus = await packageForPlan("plus", userId);
  const pro = await packageForPlan("pro", userId);
  return {
    plus: {
      plan: "plus",
      price: plus.product.priceString,
      productId: plus.product.identifier,
    },
    pro: {
      plan: "pro",
      price: pro.product.priceString,
      productId: pro.product.identifier,
    },
  };
}

export async function purchaseNativeMembership(
  plan: MembershipPlan,
  userId: string,
  idToken: string,
) {
  const selected = await packageForPlan(plan, userId);
  trackAnalyticsEvent("checkout_start", {
    tier: plan,
    product_id: selected.product.identifier,
    store: Capacitor.getPlatform(),
  });
  try {
    const result = await Purchases.purchasePackage({ aPackage: selected });
    const tier = activeTier(result.customerInfo);
    if (tier === "free")
      throw new Error("The store did not activate a membership.");
    await reconcile(idToken);
    trackAnalyticsEvent("purchase", {
      tier,
      product_id: selected.product.identifier,
      currency: selected.product.currencyCode || "",
      value: selected.product.price,
      store: Capacitor.getPlatform(),
    });
    return { tier, customerInfo: result.customerInfo };
  } catch (error) {
    const detail = error as { code?: string; userCancelled?: boolean };
    if (!detail.userCancelled)
      trackAnalyticsEvent("purchase_failed", {
        tier: plan,
        product_id: selected.product.identifier,
        error_code: detail.code || "unknown",
        store: Capacitor.getPlatform(),
      });
    throw error;
  }
}

export async function restoreNativeMembership(userId: string, idToken: string) {
  await configure(userId);
  trackAnalyticsEvent("restore_started", { store: Capacitor.getPlatform() });
  const { customerInfo } = await Purchases.restorePurchases();
  await reconcile(idToken);
  const tier = activeTier(customerInfo);
  trackAnalyticsEvent("restore_completed", {
    store: Capacitor.getPlatform(),
    tier,
    entitlement_count: Object.keys(customerInfo.entitlements.active).length,
  });
  return { tier, customerInfo };
}
